import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  generatePath,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { Can, useAuth } from '@gbs-monorepo-packages/auth';
import {
  DefaultDescriptionBenefitEducation,
  HighUserRoles,
  Logger,
  LogoutSidebarItem,
  NotViewerUsers,
  Roles,
  SidebarItem,
  formatUnknownErrorMessage,
  getRouteFrom,
  getSessionStorage,
  removeSessionStorage,
  useToast,
} from '@gbs-monorepo-packages/common';

import { SelectCompanies } from '../../components/SelectCompanies';
import { ADMIN_URL, CLIENT_URL } from '../../constants/Env';
import {
  ANALYTICS,
  CLIENT,
  CLIENTS,
  COURSES,
  COURSES_TEMPLATE,
  FOLDERS,
  LOGIN,
  MEMBERS,
  REDIRECT,
  SETTINGS,
} from '../../constants/RoutePaths';
import { EdgeStorageKey } from '../../constants/Storage';
import { useClients } from '../../hooks/useCompanies';
import { type IClientDTO } from '../../services/companies';
import { DefaultLayout } from '../DefaultLayout';
import {
  AdminPortalIcon,
  AnalyticsIcon,
  BaseLoaderContainer,
  ClientName,
  ClientPortalIcon,
  ClientsIcon,
  Container,
  CoursesIcon,
  DocumentsIcon,
  HeaderChildrenContent,
  Loading,
  MembersIcon,
  TemplatesIcon,
} from './styles';

const CoursesRoute = getRouteFrom(COURSES);
const CompaniesRoute = getRouteFrom(CLIENTS);
const SettingsRoute = getRouteFrom(SETTINGS);
const TeammatesRoute = getRouteFrom(MEMBERS);
const TemplatesRoute = getRouteFrom(COURSES_TEMPLATE);
const AnalyticsRoute = getRouteFrom(ANALYTICS);
const FolderRoute = getRouteFrom(FOLDERS);

const titleCompanies = 'Clients';
const titleCourse = 'Courses';
const titleDocuments = 'Resources';
const titleTeammates = 'Users';
const titleTemplateCourse = 'Templates';
const titleAnalytics = 'Analytics';

const RolesCanGoToCompanies = HighUserRoles;
const RolesCanSelectCompanies = RolesCanGoToCompanies;
const AlmostRoles = NotViewerUsers;
const CanSeeCourses = [Roles.EMPLOYEE];
const RolesCanRedirClient = [Roles.CLIENT];

const ParamRegex = /:\w+/;
interface ILocation extends Location {
  state: { initialAccess: boolean; from: string } | null;
}

export const CompanyLayout = (): JSX.Element => {
  const { addToast } = useToast();
  const { user, signOut } = useAuth();
  const { selectedClient, selectClientByID, selectClient } = useClients();
  const { clientId = '' } = useParams();
  const { pathname, state = null }: Partial<ILocation> =
    useLocation() as Partial<ILocation>;
  const [loadingClients, setLoadingClient] = useState(false);
  const navigate = useNavigate();
  const clientIdSelected = selectedClient?.id ?? 0;

  const handleSelectCompany = useCallback(
    (id: string, client: IClientDTO) => {
      if (!pathname) {
        Logger.error('Pathname is not defined');
        return;
      }

      const matchCompany = matchPath(
        {
          path: getRouteFrom(CLIENT),
          end: false,
        },
        pathname
      );

      selectClient(client);
      if (matchCompany) {
        const { pathname: companyPathname, pattern } = matchCompany;
        const newCompanyPathname = pattern.path.replace(ParamRegex, id);

        navigate(pathname.replace(companyPathname, newCompanyPathname));
      }
    },
    [navigate, pathname, selectClient]
  );

  const {
    CoursesPath,
    TeammatesPath,
    TemplatesPath,
    AnalyticsPath,
    DocumentsPath,
  } = useMemo(() => {
    const clientId = selectedClient?.id ?? 0;
    if (clientId <= 0 || loadingClients) {
      return {
        clientId,
        CoursesPath: '.',
        SettingsPath: '.',
        TeammatesPath: '.',
        TemplatesPath: '.',
        AnalyticsPath: '.',
        DocumentsPath: '.',
      };
    }
    return {
      clientId,
      CoursesPath: generatePath(CoursesRoute, { clientId }),
      SettingsPath: generatePath(SettingsRoute, { clientId }),
      TeammatesPath: generatePath(TeammatesRoute, { clientId }),
      TemplatesPath: generatePath(TemplatesRoute, { clientId }),
      AnalyticsPath: generatePath(AnalyticsRoute, { clientId }),
      DocumentsPath:
        clientId <= 0
          ? '.'
          : generatePath(FolderRoute, { clientId, typeFolder: 'private' }),
    };
  }, [loadingClients, selectedClient?.id]);

  useEffect(() => {
    const clientIdNum = parseInt(clientId);
    const selectedClientId = selectedClient?.id ?? 0;

    let clientToSelect =
      isNaN(clientIdNum) && !selectedClientId
        ? getSessionStorage<string>(EdgeStorageKey.CLIENT, '')
        : clientIdNum > 0 && selectedClientId !== clientIdNum
          ? clientId
          : null;

    if (
      state?.initialAccess === true &&
      !getSessionStorage<string>(EdgeStorageKey.CLIENT, '') &&
      user &&
      !state?.from?.includes('companies')
    ) {
      clientToSelect = user?.clientId != null ? String(user?.clientId) : null;
    }

    const needChangeClient = clientToSelect !== null;
    if (needChangeClient) {
      const changeClient = async () => {
        setLoadingClient(true);
        try {
          await selectClientByID(clientToSelect || String(user?.clientId));
        } catch (error) {
          addToast({
            title: 'Error on selecting client',
            description: formatUnknownErrorMessage(
              error,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
          });
          navigate(getRouteFrom(LOGIN));
        } finally {
          setLoadingClient(false);
        }
      };
      void changeClient();
    }
  }, [
    state,
    user,
    addToast,
    clientId,
    navigate,
    selectClient,
    selectClientByID,
    selectedClient?.id,
  ]);

  const handleSignOut = useCallback(async () => {
    removeSessionStorage(EdgeStorageKey.CLIENT);
    await signOut();
    selectClient(null);
  }, [signOut, selectClient]);

  return clientIdSelected > 0 ? (
    <DefaultLayout
      headerChildren={
        <HeaderChildrenContent>
          <Can
            roles={RolesCanSelectCompanies}
            alternativeChildren={
              <ClientName>{selectedClient?.name ?? 'Not found'}</ClientName>
            }
          >
            <SelectCompanies onValueChange={handleSelectCompany} />
          </Can>
        </HeaderChildrenContent>
      }
      sidebarChildren={(isShrunken) => (
        <>
          <Can roles={CanSeeCourses}>
            <SidebarItem
              dataCy={`navLink-courses${clientId}`}
              icon={<CoursesIcon />}
              isShrunken={isShrunken}
              title={titleCourse}
              type="link"
              to={clientIdSelected > 0 ? CoursesPath : REDIRECT.path}
            />
          </Can>
          <Can roles={AlmostRoles}>
            <SidebarItem
              dataCy={`navLink-courses${clientId}`}
              icon={<CoursesIcon />}
              isShrunken={isShrunken}
              title={titleCourse}
              type="link"
              to={clientIdSelected > 0 ? CoursesPath : REDIRECT.path}
            />
            <SidebarItem
              dataCy={`navLink-documents${clientId}`}
              icon={<DocumentsIcon />}
              isShrunken={isShrunken}
              title={titleDocuments}
              to={DocumentsPath}
              type="link"
            />

            <SidebarItem
              dataCy={`navLink-users${clientId}`}
              icon={<MembersIcon />}
              isShrunken={isShrunken}
              title={titleTeammates}
              type="link"
              to={clientIdSelected > 0 ? TeammatesPath : REDIRECT.path}
            />
            <Can roles={HighUserRoles}>
              <SidebarItem
                dataCy={`navLink-templates${clientId}`}
                icon={<TemplatesIcon />}
                isShrunken={isShrunken}
                title={titleTemplateCourse}
                type="link"
                to={clientIdSelected > 0 ? TemplatesPath : REDIRECT.path}
              />
            </Can>
            <SidebarItem
              dataCy={`navLink-analytics${clientId}`}
              icon={<AnalyticsIcon />}
              isShrunken={isShrunken}
              title={titleAnalytics}
              type="link"
              to={clientIdSelected > 0 ? AnalyticsPath : REDIRECT.path}
            />
          </Can>
        </>
      )}
      sidebarFooterChildren={(isShrunken) => (
        <>
          <Can roles={RolesCanGoToCompanies}>
            <SidebarItem
              dataCy={`${CompaniesRoute}`}
              icon={<ClientsIcon />}
              isShrunken={isShrunken}
              title={titleCompanies}
              type="link"
              to={clientIdSelected > 0 ? CompaniesRoute : REDIRECT.path}
              end
            />
            <SidebarItem
              dataCy={`navLink-redirectAdmin`}
              icon={<AdminPortalIcon />}
              isShrunken={isShrunken}
              title={'Admin Portal'}
              type="link"
              to={
                ADMIN_URL +
                '/redirect-login' +
                (selectedClient !== null
                  ? '?selectedClientId=' + selectedClient.id.toString()
                  : '')
              }
            />
          </Can>
          <Can roles={RolesCanRedirClient}>
            <SidebarItem
              dataCy={`navLink-redirectClient`}
              icon={<ClientPortalIcon />}
              isShrunken={isShrunken}
              title={'Client Portal'}
              type="link"
              to={
                CLIENT_URL +
                '/redirect-login' +
                (selectedClient !== null
                  ? '?selectedClientId=' + selectedClient.id.toString()
                  : '')
              }
            />
          </Can>
          <LogoutSidebarItem
            isShrunken={isShrunken}
            handleSignOut={handleSignOut}
          />
        </>
      )}
    />
  ) : (
    <Container data-cy="loading-company-layout-container">
      <BaseLoaderContainer>
        <Loading dataCy="loading-company-layout" />
      </BaseLoaderContainer>
    </Container>
  );
};
